import {
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { Router } from '@angular/router';
import { ChartExportDto } from '@sociuu/interfaces';
import * as Chart from 'chart.js';
import jsPDF from 'jspdf';
import moment from 'moment';
import {
  ApexAxisChartSeries,
  ApexChart,
  ApexDataLabels,
  ApexFill,
  ApexGrid,
  ApexLegend,
  ApexMarkers,
  ApexNoData,
  ApexStroke,
  ApexTitleSubtitle,
  ApexXAxis,
  ApexYAxis,
  ChartComponent,
} from 'ng-apexcharts';

export interface ChartOptions {
  noData: ApexNoData;
  series: ApexAxisChartSeries;
  chart: ApexChart;
  xaxis: ApexXAxis;
  yaxis: ApexYAxis;
  stroke: ApexStroke;
  fill: ApexFill;
  dataLabels: ApexDataLabels;
  markers: ApexMarkers;
  tooltip: any; // ApexTooltip;
  grid: ApexGrid;
  legend: ApexLegend;
  title: ApexTitleSubtitle;
  labels: string[];
}

@Component({
  selector: 'sociuu-line-chart',
  templateUrl: './line-chart.component.html',
  styleUrls: ['./line-chart.component.scss'],
})
export class LineChartComponent implements OnInit, OnChanges {
  @Input() chartData = [];
  @Input() chartTitle = '';
  @Input() chartSubheading = '';
  @Input() series = [];
  @Input() newSeries = [];
  @Input() label = '';
  @Input() labels = [];
  @Input() title = '';
  @Input() loadingText = '';
  @Input() menuType = 'print';
  @Input() showMenu = true;
  @Input() xAxisType: 'category' | 'datetime' | 'numeric' = 'category';
  @Input() tickAmount: number;
  @Input() showExportMenuIcons: boolean = true;
  @Output() dateFilter = new EventEmitter();

  @ViewChild('lineChart') lineChart: ElementRef;
  myChart: Chart = [];

  @ViewChild('chart', { static: false }) chart: ChartComponent;
  public chartOptions: Partial<ChartOptions>;
  imgURI;

  constructor(private _router: Router) {
    this.setChartOptions();
  }

  public ngOnChanges(changes: SimpleChanges): void {
    if (this.chart) {
      this.updateOptions({
        noData: {
          text: this.loadingText,
        },
        labels: this.labels,
        tooltip: {
          custom: this.title,
        },
        markers: {
          size: this.series?.[0]?.type == 'scatter' ? 7 : 0,
          hover: {
            sizeOffset:
              this.series?.[0]?.type == 'scatter' ? -2 : 5,
          },
        },
      });

      if (this.series?.[0]?.type == 'scatter') {
        this.updateOptions({
          grid: {
            show: this.chartData.length,
          },
          xaxis: {
            tickPlacement: 'on',
            tickAmount: 4,
            type: 'category',
            labels: {
              show: this.chartData.length,
              formatter: function (val, opts) {
                return moment(new Date(val)).format('DD/MMM/yy');
              },
              datetimeFormatter: {
                year: 'yyyy',
                month: 'MMM \'yy',
                day: 'dd MMM',
                hour: 'HH:mm'
              },
              datetimeUTC: true,
            },
            tooltip: {
              formatter: function (val, opts) {
                return moment(new Date(val)).format(
                  'MMM-DD-YYYY'
                );
              },
            },
          },
          yaxis: {
            labels: {
              show: this.chartData.length,
            },
            floating: false,
            decimalsInFloat: 0,
            tickAmount: this.tickAmount,
            min: 0,
          },
        });
      }
      this.updateSeries(this.series);
      this.chart.dataURI().then((res: any) => (this.imgURI = res.imgURI));
    }
  }

  ngOnInit(): void {
    this.setChartOptions();

    // Attach SVG fill fixer to all ApexCharts
    window['Apex'] = {
      chart: {
        events: {
          mounted: (chart: any, options?: any): void => {
            this._fixSvgFill(chart.el);
          },
          updated: (chart: any, options?: any): void => {
            this._fixSvgFill(chart.el);
          },
        },
      },
    };
  }

  setChartOptions(): void {
    this.chartOptions = {
      noData: {
        text: this.loadingText,
      },
      series: this.series,
      chart: {
        redrawOnParentResize: true,
        redrawOnWindowResize: true,
        height: '100%',
        type: 'area',
        toolbar: {
          tools: {
            download: false,
          },
        },
      },
      dataLabels: {
        enabled: false,
      },
      labels: [],
      stroke: {
        width: 1,
        curve: 'straight',
        colors: ['rgba(54, 162, 235, 1)'],
        dashArray: [0, 0, 0],
      },
      fill: {
        type: 'solid',
        colors:
          this.series.length == 1
            ? ['rgba(54, 162, 235, 0.2)']
            : [
              'rgba(68, 114, 196, 0.5)',
              'rgba(190, 81, 1, 1)',
              'rgba(217, 217, 217, 1)',
            ],
        opacity: [0.2, 0.2, 1],
      },
      title: {
        text: '',
        align: 'left',
      },
      legend: {
        tooltipHoverFormatter: function (val, opts) {
          return (
            val +
            '<strong>: ' +
            opts.w.globals.series[opts.seriesIndex][
            opts.dataPointIndex
            ] +
            '</strong>'
          );
        },
      },
      markers: {
        size: 1,
        colors: ['rgba(54, 162, 235, 0.2)'],
        strokeColors: 'rgba(54, 162, 235, 1)',
        strokeWidth: 1,
        radius: 2,
      },
      xaxis: {
        type: this.xAxisType,
        tickPlacement: 'on',
        labels: {
          trim: false,
          format: 'yyyy/MMM',
        },
        categories: [],
        tickAmount: 10,
      },
      yaxis: {
        floating: false,
        decimalsInFloat: 0,
        tickAmount: 10,
        min: 0,
      },
      tooltip: {
        custom: '',
      },
      grid: {
        borderColor: '#f1f1f1',
      },
    };
  }

  private _fixSvgFill(element: Element): void {
    // Current URL
    const currentURL = this._router.url;

    // 1. Find all elements with 'fill' attribute within the element
    // 2. Filter out the ones that doesn't have cross reference so we only left with the ones that use the 'url(#id)' syntax
    // 3. Insert the 'currentURL' at the front of the 'fill' attribute value
    Array.from(element.querySelectorAll('*[fill]'))
      .filter((el) => el.getAttribute('fill').indexOf('url(') !== -1)
      .forEach((el) => {
        const attrVal = el.getAttribute('fill');
        el.setAttribute(
          'fill',
          `url(${currentURL}${attrVal.slice(attrVal.indexOf('#'))}`
        );
      });
  }

  updateSeries(newSeries): void {
    this.chart.updateSeries(newSeries);
  }

  updateOptions(options): void {
    this.chart.updateOptions(options);
  }

  exportChart(data: ChartExportDto): void {
    const { extension, fileName } = data;
    if (extension === 'pdf') {
      const pdf = new jsPDF('l', 'px', 'a4');
      const i = new Image();
      i.src = this.imgURI;
      pdf.addImage(i, 'png', 10, 10, i.width, i.height);
      pdf.save(`${fileName}.pdf`);
    } else {
      const a = document.getElementById('ar') as HTMLAnchorElement;
      a.href = this.imgURI;
      a.download = `${fileName}.png`;
      a.click();
    }
  }

  exportFilter(date: string): void {
    this.dateFilter.emit(date);
  }
}
